import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    CircularProgress,
    Grid,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as BackIcon,
    Label as LabelIcon,
    Book as BookIcon,
    Help as HelpIcon,
    Settings as SettingsIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Star as StarIcon,
    Favorite as FavoriteIcon,
    Home as HomeIcon,
    Work as WorkIcon,
    School as SchoolIcon,
    Computer as ComputerIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Security as SecurityIcon,
    Build as BuildIcon,
    Code as CodeIcon,
    CloudQueue as CloudIcon,
    Storage as StorageIcon,
    Assignment as AssignmentIcon,
    Description as DescriptionIcon,
    Folder as FolderIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { canManageWikiArticles, canDeleteWikiCategories } from './wikiPermissions';

interface WikiCategory {
    id: number;
    name: string;
    color: string;
    icon?: string;
    description?: string;
    companyId?: number | null;
    isPublic?: boolean;
}

const availableIcons = [
    { name: 'Book', component: BookIcon, value: 'book' },
    { name: 'Help', component: HelpIcon, value: 'help' },
    { name: 'Settings', component: SettingsIcon, value: 'settings' },
    { name: 'Info', component: InfoIcon, value: 'info' },
    { name: 'Warning', component: WarningIcon, value: 'warning' },
    { name: 'Star', component: StarIcon, value: 'star' },
    { name: 'Favorite', component: FavoriteIcon, value: 'favorite' },
    { name: 'Home', component: HomeIcon, value: 'home' },
    { name: 'Work', component: WorkIcon, value: 'work' },
    { name: 'School', component: SchoolIcon, value: 'school' },
    { name: 'Computer', component: ComputerIcon, value: 'computer' },
    { name: 'Phone', component: PhoneIcon, value: 'phone' },
    { name: 'Email', component: EmailIcon, value: 'email' },
    { name: 'Security', component: SecurityIcon, value: 'security' },
    { name: 'Build', component: BuildIcon, value: 'build' },
    { name: 'Code', component: CodeIcon, value: 'code' },
    { name: 'Cloud', component: CloudIcon, value: 'cloud_queue' },
    { name: 'Storage', component: StorageIcon, value: 'storage' },
    { name: 'Assignment', component: AssignmentIcon, value: 'assignment' },
    { name: 'Description', component: DescriptionIcon, value: 'description' },
    { name: 'Folder', component: FolderIcon, value: 'folder' },
    { name: 'Label', component: LabelIcon, value: 'label' }
];

const WikiCategories: React.FC = () => {
    const [categories, setCategories] = useState<WikiCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<WikiCategory | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        color: '#1976d2',
        icon: '',
        description: '',
        isGlobal: false,
        isPublic: false
    });

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const translate = useTranslate();

    const isPlatformAdmin = permissions?.includes('ROLE_PLATFORM_ADMIN');
    const canEdit = canManageWikiArticles(permissions);
    const canDelete = canDeleteWikiCategories(permissions);

    const getIconComponent = (iconValue?: string) => {
        if (!iconValue) return null;
        const icon = availableIcons.find(i => i.value === iconValue);
        return icon ? icon.component : null;
    };

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('wiki_categories', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });
            setCategories(data);
        } catch (error) {
            notify(translate('wiki.categories.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (category?: WikiCategory) => {
        if (category) {
            setEditingCategory(category);
            setFormData({
                name: category.name,
                color: category.color,
                icon: category.icon || '',
                description: category.description || '',
                isGlobal: !category.companyId,
                isPublic: category.isPublic || false
            });
        } else {
            setEditingCategory(null);
            setFormData({
                name: '',
                color: '#1976d2',
                icon: '',
                description: '',
                isGlobal: false,
                isPublic: false
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingCategory(null);
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            notify(translate('wiki.categories.nameRequired'), { type: 'warning' });
            return;
        }

        try {
            const categoryData: any = {
                name: formData.name,
                color: formData.color,
                icon: formData.icon || null,
                description: formData.description || null,
                isPublic: formData.isPublic,
            };

            // Only platform admins can create global categories
            if (isPlatformAdmin && formData.isGlobal) {
                categoryData.companyId = null;
            }

            if (editingCategory) {
                await dataProvider.update('wiki_categories', {
                    id: editingCategory.id,
                    data: categoryData,
                    previousData: editingCategory
                });
                notify(translate('wiki.categories.updateSuccess'), { type: 'success' });
            } else {
                await dataProvider.create('wiki_categories', { data: categoryData });
                notify(translate('wiki.categories.createSuccess'), { type: 'success' });
            }

            handleCloseDialog();
            loadCategories();
        } catch (error: any) {
            notify(error?.message || translate('wiki.categories.saveError'), { type: 'error' });
        }
    };

    const handleDelete = async (category: WikiCategory) => {
        if (!window.confirm(translate('wiki.categories.confirmDelete', { name: category.name }))) {
            return;
        }

        try {
            await dataProvider.delete('wiki_categories', {
                id: category.id,
                previousData: category
            });
            notify(translate('wiki.categories.deleteSuccess'), { type: 'success' });
            loadCategories();
        } catch (error: any) {
            notify(error?.message || translate('wiki.categories.deleteError'), { type: 'error' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/wiki')}>
                    {translate('wiki.categories.back')}
                </Button>
                {canEdit && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {translate('wiki.categories.newCategory')}
                    </Button>
                )}
            </Box>

            {/* Categories Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('wiki.categories.table.name')}</TableCell>
                            <TableCell>{translate('wiki.categories.table.color')}</TableCell>
                            <TableCell>{translate('wiki.categories.table.icon')}</TableCell>
                            <TableCell>{translate('wiki.categories.table.description')}</TableCell>
                            <TableCell>{translate('wiki.categories.table.type')}</TableCell>
                            <TableCell>FAQ</TableCell>
                            {canEdit && <TableCell align="right">{translate('wiki.categories.table.actions')}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={canEdit ? 7 : 6} align="center">
                                    <Typography color="text.secondary" sx={{ py: 4 }}>
                                        {translate('wiki.categories.noCategories')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            categories.map((category) => {
                                const IconComponent = getIconComponent(category.icon);
                                return (
                                <TableRow key={category.id}>
                                    <TableCell>
                                        <Chip
                                            label={category.name}
                                            sx={{ backgroundColor: category.color, color: '#fff' }}
                                            icon={IconComponent ? <IconComponent sx={{ color: '#fff !important' }} /> : undefined}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    backgroundColor: category.color,
                                                    borderRadius: 1,
                                                    border: '1px solid #ccc'
                                                }}
                                            />
                                            {category.color}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{category.icon || '-'}</TableCell>
                                    <TableCell>{category.description || '-'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={category.companyId ? translate('wiki.categories.companyCategory') : translate('wiki.categories.globalCategory')}
                                            size="small"
                                            color={category.companyId ? 'default' : 'primary'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {category.isPublic && (
                                            <Chip
                                                label="FAQ"
                                                size="small"
                                                color="success"
                                            />
                                        )}
                                    </TableCell>
                                    {canEdit && (
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(category)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {canDelete && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDelete(category)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCategory ? translate('wiki.categories.editCategory') : translate('wiki.categories.newCategory')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label={translate('wiki.categories.form.name')}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            fullWidth
                            required
                        />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('wiki.categories.form.color')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    style={{ width: 60, height: 40, cursor: 'pointer' }}
                                />
                                <TextField
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    size="small"
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('wiki.categories.form.icon')}
                            </Typography>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(48px, 1fr))',
                                gap: 1,
                                maxWidth: '100%'
                            }}>
                                {availableIcons.map((icon) => {
                                    const IconComponent = icon.component;
                                    const isSelected = formData.icon === icon.value;
                                    return (
                                        <Tooltip title={icon.name} key={icon.value}>
                                            <IconButton
                                                onClick={() => setFormData({ ...formData, icon: icon.value })}
                                                sx={{
                                                    border: isSelected ? 2 : 1,
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    backgroundColor: isSelected ? 'primary.light' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: isSelected ? 'primary.light' : 'action.hover'
                                                    }
                                                }}
                                            >
                                                <IconComponent />
                                            </IconButton>
                                        </Tooltip>
                                    );
                                })}
                                <Tooltip title={translate('wiki.categories.form.noIcon')}>
                                    <IconButton
                                        onClick={() => setFormData({ ...formData, icon: '' })}
                                        sx={{
                                            border: !formData.icon ? 2 : 1,
                                            borderColor: !formData.icon ? 'primary.main' : 'divider',
                                            backgroundColor: !formData.icon ? 'primary.light' : 'transparent'
                                        }}
                                    >
                                        <Typography variant="body2">—</Typography>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <TextField
                            label={translate('wiki.categories.form.description')}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={3}
                            fullWidth
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <input
                                type="checkbox"
                                checked={formData.isPublic}
                                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                                id="isPublic"
                            />
                            <label htmlFor="isPublic">
                                <Box>
                                    <Typography variant="body2">
                                        {translate('wiki.categories.isPublic', 'Oeffentlich (FAQ)')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {translate('wiki.categories.isPublicHelp', 'Oeffentliche Kategorien sind ohne Login unter /faq erreichbar')}
                                    </Typography>
                                </Box>
                            </label>
                        </Box>

                        {isPlatformAdmin && !editingCategory && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="checkbox"
                                    checked={formData.isGlobal}
                                    onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked })}
                                    id="isGlobal"
                                />
                                <label htmlFor="isGlobal">
                                    <Typography variant="body2">
                                        {translate('wiki.categories.form.globalCategory')}
                                    </Typography>
                                </label>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>{translate('ra.action.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained">
                        {editingCategory ? translate('ra.action.update') : translate('ra.action.create')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WikiCategories;
